/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { useContext } from 'react';
import { ResponsiveContext } from 'src/core/utils/contexts/ResponsiveContext';
import { ResponsiveMode, ResponsiveModeType } from 'src/core/utils/hocs/withResponsive';

export type ResponsiveModeObj = {
    /**
     * A value representing the current screen size.
     */
    responsiveMode: ResponsiveModeType;
    /**
     * A boolean indicating if the window is small or greater.
     */
    isSmallOrGreater: boolean;
    /**
     * A boolean indicating if the window is medium or greater.
     */
    isMediumOrGreater: boolean;
    /**
     * A boolean indicating if the window is large or greater.
     */
    isLargeOrGreater: boolean;
    /**
     * A boolean indicating if the window is X large or greater.
     */
    isXLargeOrGreater: boolean;
    /**
     * A boolean indicating if the window is XX large or greater.
     */
    isXXLargeOrGreater: boolean;
    /**
     * A boolean indicating if the window is XXX large or greater.
     */
    isXXXLargeOrGreater: boolean;
};

/**
 * A hook for subscribing to the current screen size.
 */
export const useResponsiveMode = (): ResponsiveModeObj => {
    const responsiveMode = useContext(ResponsiveContext);

    const isSmallOrGreater = responsiveMode >= ResponsiveMode.small;
    const isMediumOrGreater = responsiveMode >= ResponsiveMode.medium;
    const isLargeOrGreater = responsiveMode >= ResponsiveMode.large;
    const isXLargeOrGreater = responsiveMode >= ResponsiveMode.xLarge;
    const isXXLargeOrGreater = responsiveMode >= ResponsiveMode.xxLarge;
    const isXXXLargeOrGreater = responsiveMode >= ResponsiveMode.xxxLarge;

    return {
        responsiveMode,
        isSmallOrGreater,
        isMediumOrGreater,
        isLargeOrGreater,
        isXLargeOrGreater,
        isXXLargeOrGreater,
        isXXXLargeOrGreater
    };
};
